import React, { useMemo, useState } from "react";
import Select, { components as selectComponents } from "react-select";
import { gridMenuBox, gridRowStyle, ROW_PADDING_X } from "./ItemGridSelect";

/**
 * Transaction Type picker for the Add and Edit Invoice screens: a searchable
 * dropdown whose menu opens as a full-width grid, like the item picker - one
 * row per scenario with its code (SN001...), description and FBR sale type in
 * columns under a header.
 *
 * It keeps SearchableSelect's contract, so the screens' handleChange is not
 * touched: onChange receives a native-select-shaped event
 *   e.target.name / e.target.value          - the scenario's code
 *   e.target.tagName === "SELECT"           - keeps upperCaseInput() off it
 *   e.target.selectedOptions[0].text        - what the field shows
 *
 * Search runs over the code, the description and the sale type.
 *
 * ScenarioGridMultiSelect, below, is the same grid for picking several - the
 * company's Assigned Scenarios.
 */

const EMPTY_OPTION = { value: "", label: "" };

const GRID_COLUMNS = [
  { key: "scenarioCode", header: "Code", min: 90, track: "0.6fr" },
  { key: "scenarioDescription", header: "Description", min: 260, track: "3fr" },
  { key: "name", header: "Sale Type", min: 180, track: "2fr" },
];

// The same columns, narrower, for a grid that opens no wider than its field
// (the company forms' half-width Assigned Scenarios).
const COMPACT_GRID_COLUMNS = [
  { key: "scenarioCode", header: "Code", min: 64, track: "0.5fr" },
  { key: "scenarioDescription", header: "Description", min: 180, track: "2.5fr" },
  { key: "name", header: "Sale Type", min: 120, track: "1.5fr" },
];

/** What the field shows once a scenario is picked: "SN001 - DESCRIPTION". */
export const scenarioLabel = (scenario) =>
  [scenario?.scenarioCode, scenario?.scenarioDescription]
    .filter(Boolean)
    .join(" - ");

const cellClass = (column) =>
  [
    "min-w-0 break-words",
    column.key === "scenarioCode" ? "font-semibold whitespace-nowrap" : "",
  ]
    .filter(Boolean)
    .join(" ");

// Both read their columns from selectProps, so the components stay the same
// objects whichever picker renders them.

/** One scenario as a row of the grid. */
const GridOption = (props) => (
  <selectComponents.Option {...props}>
    {props.selectProps.gridColumns.map((column) => (
      <div key={column.key} className={cellClass(column)}>
        {props.data.scenario?.[column.key] ?? ""}
      </div>
    ))}
  </selectComponents.Option>
);

/** The grid: a header that stays put while the rows scroll under it. */
const GridMenuList = (props) => {
  const { gridColumns } = props.selectProps;
  return (
    <selectComponents.MenuList {...props}>
      <div
        aria-hidden="true"
        className="sticky top-0 z-10 py-2 text-xs font-semibold uppercase tracking-wide text-gray-600 bg-gray-100 border-b border-gray-200"
        style={{
          ...gridRowStyle(gridColumns),
          paddingLeft: ROW_PADDING_X,
          paddingRight: ROW_PADDING_X,
        }}
      >
        {gridColumns.map((column) => (
          <div key={column.key} className={cellClass(column)}>
            {column.header}
          </div>
        ))}
      </div>
      {props.children}
    </selectComponents.MenuList>
  );
};

const SELECT_COMPONENTS = {
  IndicatorSeparator: null,
  MenuList: GridMenuList,
  Option: GridOption,
};

/** Each scenario as an option, with the text search matches against. */
const toOptions = (list, valueOf) =>
  list.map((scenario) => ({
    value: valueOf(scenario),
    label: scenarioLabel(scenario),
    scenario,
    search: [scenario.scenarioCode, scenario.scenarioDescription, scenario.name]
      .filter(Boolean)
      .join(" | ")
      .toLowerCase(),
  }));

const searchOptions = (options, query) => {
  const search = query.trim().toLowerCase();
  if (!search) return options;
  return options.filter((option) => option.search.includes(search));
};

/**
 * The grid menu's placement and row layout. `toWindowEdge` opens it from the
 * field to the right edge of the window, like the item grid; without it the
 * menu is exactly as wide as the field.
 */
const gridStyles = (columns, { toWindowEdge }) => {
  const rowStyle = gridRowStyle(columns);
  return {
    menuPortal: (base, { rect }) => ({
      ...base,
      zIndex: 9999,
      ...(toWindowEdge ? gridMenuBox(rect) : {}),
    }),
    option: (base) => ({
      ...base,
      ...rowStyle,
      paddingLeft: ROW_PADDING_X,
      paddingRight: ROW_PADDING_X,
    }),
  };
};

const WIDE_GRID_STYLES = gridStyles(GRID_COLUMNS, { toWindowEdge: true });
const FIELD_WIDTH_GRID_STYLES = gridStyles(COMPACT_GRID_COLUMNS, {
  toWindowEdge: false,
});

/**
 * The field's and grid's classes, shared by both pickers. The caller's
 * classes keep the field looking like the dropdown it replaced (width,
 * padding, radius, error border).
 */
const gridClassNames = ({ className, disabled }) => {
  const hasError = className.includes("border-red-500");
  return {
    control: ({ isFocused }) =>
      [
        className,
        "flex items-center gap-2 bg-white cursor-pointer transition-colors",
        isFocused && !hasError ? "border-blue-500" : "",
        disabled ? "bg-gray-100 text-gray-500 cursor-not-allowed" : "",
      ]
        .filter(Boolean)
        .join(" "),
    valueContainer: ({ isMulti }) =>
      isMulti
        ? "flex flex-1 flex-wrap items-center gap-1 overflow-hidden"
        : "flex flex-1 items-center overflow-hidden",
    singleValue: () => "truncate text-gray-900",
    multiValue: () =>
      "flex items-center max-w-full rounded bg-blue-50 border border-blue-200 text-sm text-gray-900",
    multiValueLabel: () => "px-2 py-0.5 truncate",
    multiValueRemove: () =>
      "px-1 self-stretch flex items-center rounded-r text-gray-500 hover:bg-red-100 hover:text-red-600",
    placeholder: () => "truncate text-gray-400",
    input: () => "m-0 p-0 text-gray-900",
    indicatorsContainer: () => "flex items-center gap-1",
    dropdownIndicator: () => "text-gray-500 hover:text-gray-700",
    clearIndicator: () => "text-gray-400 hover:text-gray-600 cursor-pointer",
    menu: () =>
      "mt-1 overflow-hidden rounded-md border border-gray-200 bg-white shadow-xl",
    menuList: () => "overflow-x-auto",
    option: ({ isSelected, isFocused }) =>
      [
        "py-2 text-sm border-b border-gray-100 cursor-pointer",
        isSelected
          ? "bg-blue-600 text-white"
          : isFocused
            ? "bg-blue-50 text-gray-900"
            : "text-gray-900",
      ].join(" "),
    noOptionsMessage: () => "px-4 py-3 text-sm text-gray-500",
  };
};

export const ScenarioGridSelect = ({
  name,
  value,
  onChange,
  onBlur,
  scenarios,
  disabled = false,
  className = "",
  placeholder,
  maxMenuHeight = 420,
  noOptionsMessage = "No matches found",
}) => {
  const list = useMemo(
    () => (Array.isArray(scenarios) ? scenarios : []),
    [scenarios],
  );

  const options = useMemo(
    () => toOptions(list, (scenario) => scenario.scenarioCode),
    [list],
  );

  const selected =
    options.find((option) => String(option.value) === String(value ?? "")) ??
    null;

  const [query, setQuery] = useState("");
  const matches = useMemo(() => searchOptions(options, query), [options, query]);

  const handleInputChange = (input, { action }) => {
    if (action === "input-change" || input !== query) setQuery(input);
  };

  // The same event SearchableSelect raises, so handleChange cannot tell the
  // two apart.
  const handleChange = (option) => {
    if (!onChange) return;
    const picked = option || EMPTY_OPTION;
    const dataset = { id: "" };
    const optionElement = {
      value: picked.value,
      label: picked.label,
      text: picked.label,
      dataset,
    };
    const target = {
      name,
      value: picked.value,
      type: "select-one",
      tagName: "SELECT",
      dataset,
      options: optionElement.value === "" ? [] : [optionElement],
      selectedOptions: [optionElement],
      selectedIndex: option ? 0 : -1,
    };
    onChange({
      target,
      currentTarget: target,
      preventDefault: () => {},
      stopPropagation: () => {},
    });
  };

  return (
    <Select
      unstyled
      inputId={name ? `searchable-select-${name}` : undefined}
      name={name}
      value={selected}
      options={matches}
      filterOption={null}
      onInputChange={handleInputChange}
      onChange={handleChange}
      onBlur={onBlur}
      isDisabled={disabled}
      isClearable
      isSearchable
      placeholder={
        placeholder ??
        (list.length === 0 ? "No Transaction Type Available" : "SELECT")
      }
      maxMenuHeight={maxMenuHeight}
      menuPlacement="auto"
      menuPortalTarget={document.body}
      noOptionsMessage={() => noOptionsMessage}
      components={SELECT_COMPONENTS}
      gridColumns={GRID_COLUMNS}
      styles={WIDE_GRID_STYLES}
      classNames={gridClassNames({ className, disabled })}
    />
  );
};

/**
 * The same grid for picking several scenarios - the company's Assigned
 * Scenarios. `value` is the picked scenarios' _ids; each shows as an
 * "SN001 - DESCRIPTION" chip in the field and leaves the grid while picked.
 * The field sits in half of the form's row, and the grid opens no wider than
 * it, on narrower columns.
 *
 * onChange receives react-select's own list of picked options
 * ({ value: _id, label, scenario }), exactly what the plain multi-select it
 * replaced passed, so the company forms' handlers are unchanged.
 */
export const ScenarioGridMultiSelect = ({
  name,
  value,
  onChange,
  onBlur,
  scenarios,
  disabled = false,
  className = "",
  placeholder = "Select...",
  maxMenuHeight = 420,
  noOptionsMessage = "No matches found",
}) => {
  const list = useMemo(
    () => (Array.isArray(scenarios) ? scenarios : []),
    [scenarios],
  );
  const options = useMemo(
    () => toOptions(list, (scenario) => scenario?._id || ""),
    [list],
  );

  const pickedIds = Array.isArray(value) ? value.map(String) : [];
  const selected = options.filter((option) =>
    pickedIds.includes(String(option.value)),
  );

  const [query, setQuery] = useState("");
  const matches = useMemo(() => searchOptions(options, query), [options, query]);

  const handleInputChange = (input, { action }) => {
    if (action === "input-change" || input !== query) setQuery(input);
  };

  return (
    <Select
      unstyled
      isMulti
      inputId={name ? `searchable-select-${name}` : undefined}
      name={name}
      value={selected}
      options={matches}
      filterOption={null}
      onInputChange={handleInputChange}
      onChange={(picked) => onChange?.(picked ? [...picked] : [])}
      onBlur={onBlur}
      isDisabled={disabled}
      isClearable
      isSearchable
      placeholder={
        list.length === 0 ? "No Transaction Type Available" : placeholder
      }
      maxMenuHeight={maxMenuHeight}
      menuPlacement="auto"
      menuPortalTarget={document.body}
      noOptionsMessage={() => noOptionsMessage}
      components={SELECT_COMPONENTS}
      gridColumns={COMPACT_GRID_COLUMNS}
      styles={FIELD_WIDTH_GRID_STYLES}
      classNames={gridClassNames({ className, disabled })}
    />
  );
};

export default ScenarioGridSelect;
